import { useState } from "react";
import {
  TableCell,
  TableRow,
  Select,
  MenuItem,
  Button,
  FormControl,
  styled,
  tableCellClasses,
  TextField,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import {
  MunicipalityChangeRequest,
  MunicipalityRequestStatus,
} from "../../../../API/types/municipalities.types";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  border: "1px solid #e0e0e0",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface MunicipalitiesTableRowProps {
  row: MunicipalityChangeRequest;
  approvalState: MunicipalityRequestStatus;
  onApprovalChange: (id: string, value: MunicipalityRequestStatus) => void;
  onActionClick: (
    id: string,
    action: MunicipalityRequestStatus,
    row: MunicipalityChangeRequest
  ) => void;
}

const MunicipalitiesTableRow = ({
  row,
  approvalState,
  onApprovalChange,
  onActionClick,
}: MunicipalitiesTableRowProps) => {
  /**
   * States
   */
  const [specTypeLocal, setSpecTypeLocal] = useState<string>(
    row.specType || "DirectFile"
  );
  const [specFiltersLocal, setSpecFiltersLocal] = useState<string[]>(
    row.specFilters || []
  );
  const [websiteUrlLocal, setWebsiteUrlLocal] = useState<string>(
    row.websiteUrl || ""
  );
  const [stateNameLocal, setStateNameLocal] = useState<string>(
    row.stateName || ""
  );
  const [municipalityNameLocal, setMunicipalityNameLocal] = useState<string>(
    row.requestedMunicipalityName || ""
  );

  /**
   * utils
   */
  const isMunicipalityEmpty = municipalityNameLocal === "";
  const isDeleteAction = row.action === "delete";

  /**
   * Callbacks
   */
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <StyledTableRow key={row.id}>
      <StyledTableCell>{row.id.slice(-4)}</StyledTableCell>
      <StyledTableCell>
        <Tooltip title={row.userName} arrow>
          <span
            style={{
              display: "block",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.userName}
          </span>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell>
        {isDeleteAction ? (
          row.stateName
        ) : (
          <TextField
            size="small"
            fullWidth
            placeholder="State"
            value={stateNameLocal}
            onChange={(e) => setStateNameLocal(e.target.value)}
          />
        )}
      </StyledTableCell>
      <StyledTableCell>
        {isDeleteAction ? (
          row.requestedMunicipalityName
        ) : (
          <TextField
            size="small"
            fullWidth
            placeholder="Municipality"
            value={municipalityNameLocal}
            onChange={(e) => setMunicipalityNameLocal(e.target.value)}
          />
        )}
      </StyledTableCell>
      <StyledTableCell align="center">
        <span
          style={{
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "bold",
            backgroundColor:
              row.action === "create"
                ? "#e8f5e8"
                : row.action === "update"
                  ? "#fff3cd"
                  : "#f8d7da",
            color:
              row.action === "create"
                ? "#155724"
                : row.action === "update"
                  ? "#856404"
                  : "#721c24",
          }}
        >
          {row.action.toLocaleUpperCase() + (row.isState ? " STATE" : "")}
        </span>
      </StyledTableCell>
      <StyledTableCell>
        {!isMunicipalityEmpty && !isDeleteAction ? (
          <Autocomplete
            multiple
            freeSolo
            value={specFiltersLocal}
            onChange={(_, newValue) => setSpecFiltersLocal(newValue)}
            options={[]}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Add filter and press Enter"
              />
            )}
          />
        ) : (
          "-"
        )}
      </StyledTableCell>
      <StyledTableCell>
        {!isMunicipalityEmpty && !isDeleteAction ? (
          <FormControl size="small" fullWidth>
            <Select
              value={specTypeLocal}
              onChange={(e) => setSpecTypeLocal(e.target.value as string)}
              displayEmpty
              sx={{ fontSize: "14px" }}
            >
              <MenuItem value={"DirectFile"}>Direct File</MenuItem>
              <MenuItem value={"Webpage"}>Webpage</MenuItem>
            </Select>
          </FormControl>
        ) : (
          "-"
        )}
      </StyledTableCell>
      <StyledTableCell>
        {!isMunicipalityEmpty && !isDeleteAction ? (
          row.action === "create" || row.action === "update" ? (
            <TextField
              size="small"
              fullWidth
              placeholder="Website URL"
              value={websiteUrlLocal}
              onChange={(e) => setWebsiteUrlLocal(e.target.value)}
              error={websiteUrlLocal !== "" && !isValidUrl(websiteUrlLocal)}
              helperText={
                websiteUrlLocal !== "" && !isValidUrl(websiteUrlLocal)
                  ? "Please enter a valid URL"
                  : ""
              }
            />
          ) : (
            <div
              style={{
                maxHeight: "40px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: "1.2",
                fontSize: "12px",
              }}
            >
              {row.websiteUrl || "-"}
            </div>
          )
        ) : (
          "-"
        )}
      </StyledTableCell>
      <StyledTableCell>
        <div
          style={{
            maxHeight: "60px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.2",
          }}
        >
          {row.comment}
        </div>
      </StyledTableCell>
      <StyledTableCell>
        <FormControl size="small" fullWidth>
          <Select
            value={approvalState}
            onChange={(e) =>
              onApprovalChange(
                row.id,
                e.target.value as MunicipalityRequestStatus
              )
            }
            displayEmpty
            sx={{ fontSize: "14px" }}
          >
            <MenuItem value="pending">
              <em>Pending</em>
            </MenuItem>
            <MenuItem value="accepted">Approve</MenuItem>
            <MenuItem value="rejected">Reject</MenuItem>
          </Select>
        </FormControl>
      </StyledTableCell>
      <StyledTableCell>
        <Button
          variant="contained"
          size="small"
          disabled={
            approvalState === "pending" ||
            (approvalState === "accepted" &&
              !isMunicipalityEmpty &&
              (row.action === "create" || row.action === "update") &&
              (!websiteUrlLocal || !isValidUrl(websiteUrlLocal)))
          }
          onClick={() => {
            if (approvalState !== "pending") {
              onActionClick(row.id, approvalState, {
                ...row,
                stateName: stateNameLocal,
                requestedMunicipalityName: municipalityNameLocal,
                specType: specTypeLocal as any,
                specFilters: specFiltersLocal,
                websiteUrl: websiteUrlLocal,
              });
            }
          }}
          sx={{
            fontSize: "12px",
            padding: "4px 8px",
            minWidth: "60px",
            backgroundColor:
              approvalState === "accepted"
                ? "#28a745"
                : approvalState === "rejected"
                  ? "#dc3545"
                  : "#6c757d",
            "&:hover": {
              backgroundColor:
                approvalState === "accepted"
                  ? "#218838"
                  : approvalState === "rejected"
                    ? "#c82333"
                    : "#6c757d",
            },
            "&:disabled": {
              backgroundColor: "#6c757d",
              color: "#fff",
            },
          }}
        >
          {approvalState === "accepted"
            ? "Approve"
            : approvalState === "rejected"
              ? "Reject"
              : "Pending"}
        </Button>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default MunicipalitiesTableRow;
